// src/services/paymentService.ts
// Payment business logic — no React here.
// Handles: subscription checks (with 5-min cache), WebP pages-URL cache (IDB +
// memory), BOG payment initiation, promo-code validation, and post-payment
// subscription refresh into useAuthStore.
//
// Security notes preserved from original:
//   Bug-1 fix: subscription cache is UID-scoped — new user never inherits
//              a previous user's cached result.
//   Bug-4 fix: access check happens BEFORE any cache read in getPaidUnitPagesUrls.
//   F-05 fix:  IDB keys are UID-scoped; cache purge captures uid BEFORE nulling.

import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth, db } from '../lib/firebase';
import { useAuthStore } from '../stores/useAuthStore';
import type { PlanId } from '../stores/usePaymentStore';

// Firebase callable functions — region must match functions/src/index.ts
const _functions = getFunctions(app, 'europe-central2');

// ── Plan metadata (single source of truth for plan UI data) ──────────────────

export interface Plan {
  id: PlanId;
  name: string;
  price: number;
  period: string;
  badge: string | null;
  highlight: boolean;
  color: string;
  savings?: string;
  features: string[];
}

export const PLANS: Plan[] = [
  {
    id: 'monthly',
    name: '1 Month',
    price: 15.99,
    period: '/month',
    badge: null,
    highlight: false,
    color: '#4a90e2',
    features: [
      'All units & exercises',
      'Full page-by-page access',
      'Audio & video lessons',
      'Progress tracking & sync',
      'Cancel anytime',
    ],
  },
  {
    id: 'three_months',
    name: '3 Months',
    price: 29.99,
    period: '/3 months',
    badge: 'Best Value',
    highlight: true,
    color: '#6c5ce7',
    savings: 'Save 37%',
    features: [
      'All units & exercises',
      'Full page-by-page access',
      'Audio & video lessons',
      'Progress tracking & sync',
      'Priority support',
    ],
  },
  {
    id: 'yearly',
    name: '1 Year',
    price: 89.99,
    period: '/year',
    badge: null,
    highlight: false,
    color: '#00b894',
    savings: 'Save 53%',
    features: [
      'All units & exercises',
      'Full page-by-page access',
      'Audio & video lessons',
      'Progress tracking & sync',
      'Priority support',
    ],
  },
];

export const PLAN_NAMES: Record<PlanId, string> = {
  monthly:      '1 Month Plan',
  three_months: '3 Months Plan',
  yearly:       '1 Year Plan',
};

// ── Constants ────────────────────────────────────────────────────────────────

/** Book IDs that are treated as "Book 1" (the one with a free unit). */
const FREE_BOOK_IDS = [
  'book1',
  'esp-for-sport',
  'espforsport',
  'start-book1',
  '4diR8W5HJFfkFKtc4VI6',
];

const SUBSCRIPTION_CACHE_TTL_MS = 5 * 60 * 1000;       // 5 minutes
const PAGES_CACHE_TTL_MS        = 23 * 60 * 60 * 1000; // 23 hours
const IDB_TTL_MS                = 23 * 60 * 60 * 1000; // 23 hours
const IDB_NAME                  = 'espPagesCache';
const IDB_STORE                 = 'pages';
const IDB_VERSION               = 1;

// ── Types ────────────────────────────────────────────────────────────────────

export interface CachedSubscription {
  isActive: boolean;
  status?: 'active' | 'inactive' | 'expired';
  plan?: PlanId | null;
  endDate?: Date | null;
  isLifetime?: boolean;
  promoCode?: string;
}

export interface PromoResult {
  valid: boolean;
  isFree: boolean;
  finalPrice: number | null;
  discountDisplay: string;
  message?: string;
}

// ── In-memory subscription cache (module-level singleton) ────────────────────

let _subscriptionCache: CachedSubscription | null = null;
let _subscriptionCachedAt                         = 0;
let _subscriptionCachedUid: string | null         = null; // Bug-1 fix

// ── In-memory pages URL cache ────────────────────────────────────────────────

const _pagesUrlCache = new Map<string, { pages: string[]; expiresAt: number }>();

// ── IndexedDB helpers ────────────────────────────────────────────────────────

let _idbPromise: Promise<IDBDatabase> | null = null;

function _openIDB(): Promise<IDBDatabase> {
  if (_idbPromise) return _idbPromise;
  _idbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const database = req.result;
      if (!database.objectStoreNames.contains(IDB_STORE)) {
        database.createObjectStore(IDB_STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => {
      _idbPromise = null; // allow retry on next call
      reject(req.error);
    };
  });
  return _idbPromise;
}

/** F-05 fix: IDB key is UID-scoped to prevent cross-user cache leakage. */
async function _getPagesFromIDB(bookId: string, unitId: string): Promise<string[] | null> {
  try {
    const database = await _openIDB();
    const uid      = auth.currentUser?.uid ?? 'anon';

    const entry = await new Promise<
      { key: string; pages: string[]; cachedAt: number } | undefined
    >((resolve, reject) => {
      const tx    = database.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req   = store.get(`${uid}_${bookId}_${unitId}`);
      req.onsuccess = () =>
        resolve(req.result as { key: string; pages: string[]; cachedAt: number } | undefined);
      req.onerror = () => reject(req.error);
    });

    if (!entry) return null;
    if (Date.now() - (entry.cachedAt || 0) > IDB_TTL_MS) return null; // expired
    return entry.pages;
  } catch {
    return null;
  }
}

/** F-05 fix: IDB key is UID-scoped. */
async function _savePagesToIDB(bookId: string, unitId: string, pages: string[]): Promise<void> {
  try {
    const database = await _openIDB();
    const uid      = auth.currentUser?.uid ?? 'anon';

    await new Promise<void>((resolve, reject) => {
      const tx    = database.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      store.put({ key: `${uid}_${bookId}_${unitId}`, pages, cachedAt: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror    = () => reject(tx.error);
    });
  } catch {
    // Non-critical — graceful degradation
  }
}

// ── Access control ───────────────────────────────────────────────────────────

/**
 * Returns true ONLY for unit1 of book1 (and its known aliases).
 * Everything else requires an active subscription.
 */
export function isFreeUnit(bookId: string, unitId: string): boolean {
  const isFirstBook =
    !bookId ||
    FREE_BOOK_IDS.includes(bookId) ||
    FREE_BOOK_IDS.includes(bookId.toLowerCase());
  const isUnit1 = unitId === 'unit1' || unitId === '1';
  return isFirstBook && isUnit1;
}

/**
 * Checks subscription status with a 5-minute UID-scoped cache.
 * Primary: Netlify check-subscription function (server-verified).
 * Fallback: Direct Firestore read (local dev / network issues).
 *
 * Bug-1 fix: cache is validated against `uid` before being returned.
 */
export async function getCachedSubscription(uid: string): Promise<CachedSubscription> {
  const now = Date.now();

  // Return cached result if it belongs to THIS user and is still fresh
  if (
    _subscriptionCache !== null &&
    _subscriptionCachedUid === uid &&
    now - _subscriptionCachedAt < SUBSCRIPTION_CACHE_TTL_MS
  ) {
    return _subscriptionCache;
  }

  // ── Method 1: Firebase callable (server-side auth verification) ──────────
  try {
    const user = auth.currentUser;
    if (user) {
      const checkSubscriptionFn = httpsCallable<
        Record<string, never>,
        { hasActiveSubscription: boolean; subscription: Record<string, unknown> | null; hasFullAccess: boolean }
      >(_functions, 'checkSubscription');

      const result = await checkSubscriptionFn({});
      const data   = result.data;

      const sub: CachedSubscription = data.hasActiveSubscription
        ? { isActive: true, ...(data.subscription as Partial<CachedSubscription>) }
        : { isActive: false };

      _subscriptionCache     = sub;
      _subscriptionCachedAt  = now;
      _subscriptionCachedUid = uid; // Bug-1 fix
      return sub;
    }
  } catch {
    console.warn('[paymentService] checkSubscription unavailable, using Firestore fallback');
  }

  // ── Method 2: Direct Firestore (local dev fallback) ───────────────────────
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const sub      = userData.subscription;

      if (sub && sub.status === 'active') {
        // Always call .toDate() on Firestore Timestamps
        const endDate: Date | null  = sub.endDate   ? sub.endDate.toDate()   : null;
        const isActive = !endDate || endDate > new Date() || sub.isLifetime === true;

        const subData: CachedSubscription = {
          isActive,
          status:     sub.status ?? 'inactive',
          plan:       sub.plan   ?? null,
          endDate,
          isLifetime: sub.isLifetime,
          promoCode:  sub.promoCode,
        };

        _subscriptionCache    = subData;
        _subscriptionCachedAt = now;
        _subscriptionCachedUid = uid; // Bug-1 fix
        return subData;
      }
    }
  } catch (e) {
    console.warn('[paymentService] Firestore subscription check failed:', e);
  }

  return { isActive: false };
}

/**
 * Returns true if the current user can access a given unit.
 * Checks admin `hasFullAccess` flag first, then subscription cache.
 */
export async function canAccessUnit(bookId: string, unitId: string): Promise<boolean> {
  if (isFreeUnit(bookId, unitId)) return true;

  const user = auth.currentUser;
  if (!user || user.isAnonymous) return false;

  // Admin override — hasFullAccess bypasses subscription requirement
  const profile = useAuthStore.getState().profile;
  if (profile?.hasFullAccess === true) return true;

  const sub = await getCachedSubscription(user.uid);
  return sub.isActive === true;
}

/**
 * Clears subscription + pages caches and purges this user's IDB entries.
 *
 * F-05 fix: uidToPurge is captured BEFORE any nulling so the IDB purge
 * always targets the correct user — never falls back to 'anon' by mistake.
 */
export function invalidateSubscriptionCache(): void {
  const uidToPurge = _subscriptionCachedUid ?? 'anon'; // F-05 fix: capture first

  _subscriptionCache    = null;
  _subscriptionCachedAt = 0;
  _subscriptionCachedUid = null;
  _pagesUrlCache.clear();

  // Purge this user's IDB page-URL cache entries (best-effort)
  try {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onsuccess = () => {
      const database = req.result;
      const tx       = database.transaction(IDB_STORE, 'readwrite');
      const store    = tx.objectStore(IDB_STORE);
      const allKeys  = store.getAllKeys();
      allKeys.onsuccess = () => {
        (allKeys.result as string[])
          .filter((k) => k.startsWith(`${uidToPurge}_`))
          .forEach((k) => store.delete(k));
      };
    };
  } catch {
    // Non-critical
  }
}

// ── Pages URL retrieval ──────────────────────────────────────────────────────

/**
 * Returns the array of WebP page URLs for a paid unit.
 * Flow: access check → IDB cache → memory cache → Firestore unitPages.
 *
 * Bug-4 fix: access check MUST come before ANY cache read so a cached
 * result from a previous premium user can never be served to a non-premium user.
 */
export async function getPaidUnitPagesUrls(bookId: string, unitId: string): Promise<string[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  // Bug-4 fix: access check first
  const hasAccess = await canAccessUnit(bookId, unitId);
  if (!hasAccess) throw new Error('ACCESS_DENIED');

  // 1. IDB cache (0 MB Firebase on repeat visits)
  const cachedPages = await _getPagesFromIDB(bookId, unitId);
  if (cachedPages) {
    const cacheKey = `${bookId}_${unitId}`;
    if (!_pagesUrlCache.has(cacheKey)) {
      _pagesUrlCache.set(cacheKey, { pages: cachedPages, expiresAt: Date.now() + PAGES_CACHE_TTL_MS });
    }
    return cachedPages;
  }

  // 2. Memory cache (same session)
  const cacheKey  = `${bookId}_${unitId}`;
  const memCached = _pagesUrlCache.get(cacheKey);
  if (memCached && Date.now() < memCached.expiresAt) return memCached.pages;

  // 3. Firestore unitPages array
  const bookDoc = await getDoc(doc(db, 'books', bookId));
  if (!bookDoc.exists()) throw new Error(`Book ${bookId} not found`);
  const data  = bookDoc.data();
  const pages = data?.unitPages?.[unitId] as string[] | undefined;
  if (!pages || !Array.isArray(pages) || pages.length === 0) {
    throw new Error('PAGES_NOT_UPLOADED');
  }

  // 4. Populate both caches
  await _savePagesToIDB(bookId, unitId, pages);
  _pagesUrlCache.set(cacheKey, { pages, expiresAt: Date.now() + PAGES_CACHE_TTL_MS });
  return pages;
}

/**
 * Returns the WebP page URL array for the free unit (unit1/book1).
 * Does not require authentication. Uses IDB cache to save Firestore reads.
 */
export async function getFreeUnitPagesUrls(
  bookId: string,
  unitId: string,
): Promise<string[] | null> {
  // Try IDB first (saves Firestore reads on repeat visits)
  const cachedPages = await _getPagesFromIDB(bookId, unitId);
  if (cachedPages) return cachedPages;

  try {
    const bookDoc = await getDoc(doc(db, 'books', bookId));
    if (bookDoc.exists()) {
      const data  = bookDoc.data();
      const pages = data?.unitPages?.[unitId] as string[] | undefined;
      if (pages && Array.isArray(pages) && pages.length > 0) {
        // Non-blocking background cache
        _savePagesToIDB(bookId, unitId, pages).catch(() => {});
        return pages;
      }
    }
  } catch {
    // Non-critical
  }
  return null;
}

// ── Payment flow ─────────────────────────────────────────────────────────────

/**
 * Calls the bog-create-order Netlify function and redirects the browser to
 * the BOG checkout page. Throws on any error (caller shows error view).
 */
export async function initiatePlanPayment(
  planType: PlanId,
  promoCode?: string,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('NOT_AUTHENTICATED');

  const createBogOrderFn = httpsCallable<
    { planType: string; returnUrl: string; promoCode?: string },
    { approvalUrl: string }
  >(_functions, 'createBogOrder');

  const payload: { planType: string; returnUrl: string; promoCode?: string } = {
    planType,
    returnUrl: `${window.location.origin}/payment-success`,
  };
  if (promoCode) payload.promoCode = promoCode;

  const result = await createBogOrderFn(payload);
  const { approvalUrl } = result.data;

  if (!approvalUrl) {
    throw new Error('Could not start payment. Please try again.');
  }

  // Redirect to BOG checkout
  window.location.href = approvalUrl;
}

/**
 * Validates a promo code via the validate-promo Netlify function.
 * If the code grants 100% off, the subscription is activated server-side
 * (the function writes to Firestore directly). The caller is responsible
 * for invalidating the local cache and refreshing the store.
 */
export async function validatePromoCode(
  code: string,
  planType: PlanId,
): Promise<PromoResult> {
  const user = auth.currentUser;
  if (!user) throw new Error('NOT_AUTHENTICATED');

  try {
    const validatePromoFn = httpsCallable<
      { promoCode: string; planType: string },
      { valid: boolean; isFree?: boolean; finalPrice?: number; discountDisplay?: string; message?: string; activated?: boolean }
    >(_functions, 'validatePromo');

    const result = await validatePromoFn({ promoCode: code, planType });
    const data   = result.data;

    if (!data.valid) {
      return {
        valid:           false,
        isFree:          false,
        finalPrice:      null,
        discountDisplay: '',
        message:         data.message ?? 'Invalid or expired promo code.',
      };
    }

    return {
      valid:           true,
      isFree:          data.isFree === true,
      finalPrice:      data.finalPrice ?? null,
      discountDisplay: data.discountDisplay ?? '',
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Invalid or expired promo code.';
    return { valid: false, isFree: false, finalPrice: null, discountDisplay: '', message };
  }
}

// ── Post-payment store refresh ───────────────────────────────────────────────

/**
 * Reads the user's Firestore document and updates useAuthStore.profile
 * with the latest subscription data. Call this after:
 *   – a 100%-off promo activates access
 *   – the user returns from a successful BOG payment
 */
export async function refreshSubscriptionInStore(): Promise<void> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) return;

  invalidateSubscriptionCache();

  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) return;

    const data    = userDoc.data();
    const profile = useAuthStore.getState().profile;
    if (!profile) return;

    const sub = data.subscription;
    if (!sub) return;

    // Always call .toDate() on Firestore Timestamps
    const endDate: Date | null   = sub.endDate   ? sub.endDate.toDate()   : null;
    const startDate: Date | null = sub.startDate ? sub.startDate.toDate() : null;

    useAuthStore.getState().setProfile({
      ...profile,
      hasFullAccess: data.hasFullAccess ?? profile.hasFullAccess,
      subscription:  {
        status:     sub.status   ?? 'inactive',
        plan:       sub.plan     ?? null,
        startDate,
        endDate,
        isLifetime: sub.isLifetime,
        promoCode:  sub.promoCode,
      },
    });
  } catch (e) {
    console.warn('[paymentService] Could not refresh subscription in store:', e);
  }
}

/**
 * Calls the getPdfUrl Cloud Function to obtain a 24-hour signed URL for a
 * paid PDF unit. Throws HttpsError 'permission-denied' if no active subscription.
 */
export async function getPdfUrl(bookId: string, unitId: string): Promise<string> {
  const getPdfUrlFn = httpsCallable<
    { bookId: string; unitId: string },
    { signedUrl: string; expiresIn: number }
  >(_functions, 'getPdfUrl');

  const result = await getPdfUrlFn({ bookId, unitId });
  return result.data.signedUrl;
}

/**
 * Computes a local expiry date estimate for a plan.
 * Used as an immediate placeholder while the real Firestore date is fetched.
 */
export function estimatePlanExpiry(planId: PlanId): string {
  const d = new Date();
  if (planId === 'monthly')      d.setMonth(d.getMonth() + 1);
  if (planId === 'three_months') d.setMonth(d.getMonth() + 3);
  if (planId === 'yearly')       d.setFullYear(d.getFullYear() + 1);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}